import type { Frame, Layer, ToolMode } from "#canvas/types.ts";
import { MAX_FRAME_COUNT } from "#puf/constants.ts";
import { emptyFrameLayers } from "#puf/helpers.ts";
import { clampStateFrames } from "#puf/machine.ts";
import {
  applyAddState,
  applyAddTransition,
  applyDeleteState,
  applyDeleteTransition,
  applyMoveState,
  applyRenameState,
  applySetEntryActions,
  applySetExitActions,
  applySetInitial,
  applyUpdateTransition,
} from "#puf/machines/flow.ts";
import type { ScaffoldAnimationSetInput as ScaffoldAnimationSetInputType } from "@pixelpuf/schemas/ai";
import { BrushSizeSchema } from "@pixelpuf/schemas/brush";
import { FpsSchema, HexColorSchema, OnionOpacitySchema, ProjectNameSchema } from "@pixelpuf/schemas/primitives";
import type { Fps, ProjectName } from "@pixelpuf/schemas/primitives";
import type { CatalogRef, MachineLayout, PxpfTransition, StateMachine } from "@pixelpuf/schemas/pxpf";
import { type FrameRange, frameRange, type StateFrameRanges } from "@pixelpuf/schemas/state";
import { clampActiveFrame, resolveActiveLayerId } from "@pixelpuf/schemas/store";
import { createStore } from "@xstate/store";
import { persist } from "@xstate/store/persist";
import { reset } from "@xstate/store/reset";
import { undoRedo } from "@xstate/store/undo";
import { INITIAL_CONTEXT, type PendingProposal, type StoreContext } from "./context.ts";
import { safeAccept } from "./validators.ts";

/** Empty machine seed used when adding the first state to a fresh project. */
const EMPTY_MACHINE: StateMachine = { initial: "", transitions: [] };

/**
 * Storage adapter passed to `persist`. In the browser this is the real
 * `localStorage`; in any non-DOM environment (bun:test, SSR, node scripts) it
 * falls back to an in-memory stub so importing this module never throws on a
 * missing global. Without this, `@xstate/store/persist` references `localStorage`
 * at module load and crashes test runners that don't preload a DOM shim.
 */
function makeMemoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear() {
      map.clear();
    },
    getItem(key) {
      return map.get(key) ?? null;
    },
    key(i) {
      return [...map.keys()][i] ?? null;
    },
    removeItem(key) {
      map.delete(key);
    },
    setItem(key, value) {
      map.set(key, value);
    },
  };
}

const persistStorage: Storage = typeof globalThis.localStorage !== "undefined"
  ? globalThis.localStorage
  : makeMemoryStorage();

/**
 * Event types whose effects are NOT undoable. Includes UI scratch (tool, color,
 * active frame/layer), prefs (fps, onion), derived writes (thumb.update is
 * downstream of frame.update), layout-only edits (state.move, Phase 4), and
 * editor selection (Phase 4).
 */
const UI_SCRATCH_EVENTS = new Set<string>([
  "tab.set",
  "mode.set",
  "color.set",
  "brushSize.set",
  "shapeFill.set",
  "activeFrame.set",
  "activeLayerId.set",
  "onion.toggle",
  "onionOpacity.set",
  "fps.set",
  "thumb.update",
  "state.move",
  "editor.selectState",
  "editor.selectTransition",
  "editor.clearSelection",
  "proposal.set",
  "proposal.clear",
]);

function moveItem<T>(arr: readonly T[], from: number, to: number): T[] {
  const next = arr.slice();
  const removed = next.splice(from, 1);
  next.splice(to, 0, ...removed);
  return next;
}

function withoutKey<V>(record: Record<string, V>, key: string): Record<string, V> {
  return Object.fromEntries(Object.entries(record).filter(([entryKey]) => entryKey !== key));
}

/** Payload for the bulk `project.load` event — replaces the multi-setter chain in applyPxpf. */
interface ProjectLoadPayload {
  frames: Frame[];
  fps: Fps;
  projectName: ProjectName;
  stateFrames: StateFrameRanges;
  stateMachine?: StateMachine | null;
  machineLayout?: MachineLayout;
}

export const store = createStore({
  context: INITIAL_CONTEXT,
  on: {
    // ─── UI scratch ───
    "tab.set": (ctx, e: { tab: StoreContext["tab"] }) => ({ ...ctx, tab: e.tab }),
    "mode.set": (ctx, e: { mode: ToolMode }) => ({ ...ctx, mode: e.mode }),
    "color.set": (ctx, e: { color: unknown }) => ({
      ...ctx,
      color: safeAccept(HexColorSchema, e.color, ctx.color, "color"),
    }),
    "brushSize.set": (ctx, e: { size: unknown }) => ({
      ...ctx,
      brushSize: safeAccept(BrushSizeSchema, e.size, ctx.brushSize, "brushSize"),
    }),
    "shapeFill.set": (ctx, e: { on: boolean }) => ({ ...ctx, shapeFill: e.on }),
    "activeFrame.set": (ctx, e: { index: number | ((p: number) => number) }) => {
      const requested = typeof e.index === "function" ? e.index(ctx.activeFrame) : e.index;
      return { ...ctx, activeFrame: clampActiveFrame(requested, ctx.frames.length) };
    },
    "activeLayerId.set": (ctx, e: { id: string }) => {
      const layers = ctx.frames[ctx.activeFrame] ?? [];
      return { ...ctx, activeLayerId: resolveActiveLayerId(layers, e.id) };
    },

    // ─── Prefs ───
    "onion.toggle": (ctx) => ({ ...ctx, onion: !ctx.onion }),
    "onionOpacity.set": (ctx, e: { value: unknown }) => ({
      ...ctx,
      onionOpacity: safeAccept(OnionOpacitySchema, e.value, ctx.onionOpacity, "onionOpacity"),
    }),
    "fps.set": (ctx, e: { fps: unknown }) => ({
      ...ctx,
      fps: safeAccept(FpsSchema, e.fps, ctx.fps, "fps"),
    }),

    // ─── Frame ops (multi-slice → ONE event each so undo treats as atomic) ───
    "frame.add": (ctx) => {
      if (ctx.frames.length >= MAX_FRAME_COUNT) return ctx;
      return {
        ...ctx,
        frames: [...ctx.frames, emptyFrameLayers()],
        frameIds: [...ctx.frameIds, crypto.randomUUID()],
        thumbs: [...ctx.thumbs, ""],
      };
    },
    "frame.remove": (ctx, e: { index: number }) => {
      if (ctx.frames.length <= 1) return ctx;
      if (e.index < 0 || e.index >= ctx.frames.length) return ctx;
      const frames = ctx.frames.filter((_, i) => i !== e.index);
      const frameIds = ctx.frameIds.filter((_, i) => i !== e.index);
      const thumbs = ctx.thumbs.filter((_, i) => i !== e.index);
      return {
        ...ctx,
        frames,
        frameIds,
        thumbs,
        activeFrame: clampActiveFrame(ctx.activeFrame, frames.length),
        stateFrames: clampStateFrames(ctx.stateFrames, frames.length),
      };
    },
    "frame.reorder": (ctx, e: { from: number; to: number }) => {
      const len = ctx.frames.length;
      if (e.from === e.to) return ctx;
      if (e.from < 0 || e.from >= len || e.to < 0 || e.to >= len) return ctx;
      const frames = moveItem(ctx.frames, e.from, e.to);
      const frameIds = moveItem(ctx.frameIds, e.from, e.to);
      const thumbs = moveItem(ctx.thumbs, e.from, e.to);
      const indices = moveItem(
        Array.from({ length: len }, (_, i) => i),
        e.from,
        e.to,
      );
      return {
        ...ctx,
        frames,
        frameIds,
        thumbs,
        activeFrame: indices.indexOf(ctx.activeFrame),
      };
    },
    "frame.update": (ctx, e: { index: number; layers: Layer[] }) => {
      if (e.index < 0 || e.index >= ctx.frames.length) return ctx;
      const next = ctx.frames.slice();
      next[e.index] = e.layers;
      return { ...ctx, frames: next };
    },
    "frame.replaceMany": (ctx, e: { entries: { frameIndex: number; layers: Layer[] }[] }) => {
      if (e.entries.length === 0) return ctx;
      const next = ctx.frames.slice();
      let mutated = false;
      for (const entry of e.entries) {
        if (entry.frameIndex < 0 || entry.frameIndex >= next.length) continue;
        next[entry.frameIndex] = entry.layers;
        mutated = true;
      }
      if (!mutated) return ctx;
      return { ...ctx, frames: next };
    },
    "thumb.update": (ctx, e: { index: number; url: string }) => {
      if (e.index < 0 || e.index >= ctx.thumbs.length) return ctx;
      const next = ctx.thumbs.slice();
      next[e.index] = e.url;
      return { ...ctx, thumbs: next };
    },
    "frames.replace": (ctx, e: { frames: Frame[] }) => {
      const frames = e.frames.length > 0 ? e.frames : [emptyFrameLayers()];
      return {
        ...ctx,
        frames,
        frameIds: frames.map(() => crypto.randomUUID()),
      };
    },
    "thumbs.replace": (ctx, e: { thumbs: string[] }) => ({ ...ctx, thumbs: e.thumbs }),

    // ─── Project meta ───
    "project.rename": (ctx, e: { name: unknown }) => ({
      ...ctx,
      projectName: safeAccept(ProjectNameSchema, e.name, ctx.projectName, "projectName"),
    }),
    "stateFrames.set": (ctx, e: { ranges: StateFrameRanges }) => ({
      ...ctx,
      stateFrames: clampStateFrames(e.ranges, ctx.frames.length),
    }),
    "stateFrames.upsert": (ctx, e: { id: string; range: FrameRange }) => ({
      ...ctx,
      stateFrames: clampStateFrames({ ...ctx.stateFrames, [e.id]: e.range }, ctx.frames.length),
    }),
    "stateFrames.removeKey": (ctx, e: { id: string }) => {
      const next = withoutKey(ctx.stateFrames, e.id);
      return { ...ctx, stateFrames: next };
    },

    // ─── Machine semantics (undoable) ───
    "machine.replace": (ctx, e: { machine: StateMachine | null; layout?: MachineLayout }) => ({
      ...ctx,
      stateMachine: e.machine,
      machineLayout: e.layout ?? ctx.machineLayout,
    }),
    "state.add": (ctx, e: { id: string; position: { x: number; y: number } }) => {
      const base = ctx.stateMachine ?? EMPTY_MACHINE;
      const withState = applyAddState(base, e.id);
      // applyAddState returns same ref on collision — collision is a no-op
      // UNLESS we're seeding a fresh machine (initial === "" still needs the
      // initial pointer set, which can't happen via applyAddState alone).
      if (withState === ctx.stateMachine && base.initial !== "") return ctx;
      // First state of a fresh machine becomes initial.
      const machine = withState.initial === "" ? applySetInitial(withState, e.id) : withState;
      return {
        ...ctx,
        stateMachine: machine,
        machineLayout: applyMoveState(ctx.machineLayout, e.id, e.position),
        stateFrames: e.id in ctx.stateFrames
          ? ctx.stateFrames
          : { ...ctx.stateFrames, [e.id]: frameRange(0, 0) },
      };
    },
    "state.rename": (ctx, e: { oldId: string; newId: string }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applyRenameState(ctx.stateMachine, e.oldId, e.newId);
      if (machine === ctx.stateMachine) return ctx;
      // Move the layout + stateFrames keys atomically.
      const oldPos = ctx.machineLayout[e.oldId];
      const layout = withoutKey(ctx.machineLayout, e.oldId);
      if (oldPos) layout[e.newId] = oldPos;
      const oldRange = ctx.stateFrames[e.oldId];
      const stateFrames = withoutKey(ctx.stateFrames, e.oldId);
      if (oldRange) stateFrames[e.newId] = oldRange;
      return { ...ctx, stateMachine: machine, machineLayout: layout, stateFrames };
    },
    "state.delete": (ctx, e: { id: string }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applyDeleteState(ctx.stateMachine, e.id);
      if (machine === ctx.stateMachine) return ctx;
      const layout = withoutKey(ctx.machineLayout, e.id);
      const stateFrames = withoutKey(ctx.stateFrames, e.id);
      // Drop selection if it pointed at the deleted state.
      const selectedStateId = ctx.selectedStateId === e.id ? null : ctx.selectedStateId;
      return { ...ctx, stateMachine: machine, machineLayout: layout, stateFrames, selectedStateId };
    },
    "state.setInitial": (ctx, e: { id: string }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applySetInitial(ctx.stateMachine, e.id);
      if (machine === ctx.stateMachine) return ctx;
      return { ...ctx, stateMachine: machine };
    },
    "state.setEntryActions": (ctx, e: { id: string; actions: CatalogRef[] }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applySetEntryActions(ctx.stateMachine, e.id, e.actions);
      if (machine === ctx.stateMachine) return ctx;
      return { ...ctx, stateMachine: machine };
    },
    "state.setExitActions": (ctx, e: { id: string; actions: CatalogRef[] }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applySetExitActions(ctx.stateMachine, e.id, e.actions);
      if (machine === ctx.stateMachine) return ctx;
      return { ...ctx, stateMachine: machine };
    },
    "transition.add": (ctx, e: { transition: PxpfTransition }) => {
      const base = ctx.stateMachine ?? EMPTY_MACHINE;
      const machine = applyAddTransition(base, e.transition);
      return { ...ctx, stateMachine: machine };
    },
    "transition.update": (ctx, e: { index: number; patch: Partial<PxpfTransition> }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applyUpdateTransition(ctx.stateMachine, e.index, e.patch);
      if (machine === ctx.stateMachine) return ctx;
      return { ...ctx, stateMachine: machine };
    },
    "transition.delete": (ctx, e: { index: number }) => {
      if (!ctx.stateMachine) return ctx;
      const machine = applyDeleteTransition(ctx.stateMachine, e.index);
      if (machine === ctx.stateMachine) return ctx;
      // Clear transition selection on any delete: the selected index either
      // points at the deleted transition (now gone) or at one whose index has
      // shifted (any transition past `e.index` decremented). Either way the
      // selection is no longer trustworthy.
      return { ...ctx, stateMachine: machine, selectedTransitionIndex: null };
    },

    // ─── Machine layout (skipped from undo) ───
    "state.move": (ctx, e: { id: string; position: { x: number; y: number } }) => ({
      ...ctx,
      machineLayout: applyMoveState(ctx.machineLayout, e.id, e.position),
    }),

    // ─── Editor scratch (skipped from undo) ───
    "editor.selectState": (ctx, e: { id: string | null }) => ({
      ...ctx,
      selectedStateId: e.id,
      selectedTransitionIndex: null,
    }),
    "editor.selectTransition": (ctx, e: { index: number | null }) => ({
      ...ctx,
      selectedTransitionIndex: e.index,
      selectedStateId: null,
    }),
    "editor.clearSelection": (ctx) => ({
      ...ctx,
      selectedStateId: null,
      selectedTransitionIndex: null,
    }),

    // ─── Atomic character archetype: states + transitions + initial + frame
    //    ranges + (resized) frame buffer in ONE undoable transaction. ───
    "machine.scaffold": (ctx, e: { input: ScaffoldAnimationSetInputType }) => applyScaffold(ctx, e.input),

    // ─── AI proposal: set/clear are UI scratch (skipped from undo);
    //    apply runs the same scaffold reducer in ONE undoable transition,
    //    additionally clearing the pendingProposal field. ───
    "proposal.set": (ctx, e: { proposal: PendingProposal }) => ({
      ...ctx,
      pendingProposal: e.proposal,
    }),
    "proposal.clear": (ctx) => ({ ...ctx, pendingProposal: null }),
    "proposal.apply": (ctx) => {
      const pending = ctx.pendingProposal;
      if (!pending) return ctx;
      const next = applyScaffold(ctx, pending.proposal);
      return { ...next, pendingProposal: null };
    },

    // ─── Bulk: replaces the multi-setter chain in applyPxpf ───
    "project.load": (ctx, e: { payload: ProjectLoadPayload }) => {
      const { payload } = e;
      const slicedFrames = payload.frames.slice(0, MAX_FRAME_COUNT);
      const frames = slicedFrames.length > 0 ? slicedFrames : [emptyFrameLayers()];
      const firstLayerId = frames[0]?.[0]?.id ?? "L1";
      return {
        ...ctx,
        frames,
        frameIds: frames.map(() => crypto.randomUUID()),
        thumbs: frames.map(() => ""),
        projectName: safeAccept(ProjectNameSchema, payload.projectName, ctx.projectName, "projectName"),
        fps: safeAccept(FpsSchema, payload.fps, ctx.fps, "fps"),
        activeFrame: 0,
        activeLayerId: firstLayerId,
        stateFrames: clampStateFrames(payload.stateFrames, frames.length),
        stateMachine: payload.stateMachine ?? ctx.stateMachine,
        machineLayout: payload.machineLayout ?? ctx.machineLayout,
      };
    },
  },
})
  .with(reset({
    to: (initial, current) => ({
      ...initial,
      // Preserve user prefs and UI scratch through `New Project`.
      tab: current.tab,
      mode: current.mode,
      color: current.color,
      brushSize: current.brushSize,
      shapeFill: current.shapeFill,
      onion: current.onion,
      onionOpacity: current.onionOpacity,
      fps: current.fps,
    }),
  }))
  .with(undoRedo({
    skipEvent: (event) => UI_SCRATCH_EVENTS.has(event.type),
    getTransactionId: (event) => event.type === "project.load" ? `load:${performance.now()}` : undefined,
  }))
  .with(persist({
    name: "pixelpuf:store",
    version: 1,
    throttle: 800,
    storage: persistStorage,
    pick: (ctx) => ({
      // Project
      frames: ctx.frames,
      frameIds: ctx.frameIds,
      thumbs: ctx.thumbs,
      projectName: ctx.projectName,
      stateFrames: ctx.stateFrames,
      stateMachine: ctx.stateMachine,
      machineLayout: ctx.machineLayout,
      // Prefs ride along under the same key
      fps: ctx.fps,
      onion: ctx.onion,
      onionOpacity: ctx.onionOpacity,
      color: ctx.color,
      brushSize: ctx.brushSize,
      mode: ctx.mode,
    }),
  }));

/**
 * Pure reducer for `machine.scaffold` and `proposal.apply`. Builds a brand
 * new context from a `ScaffoldAnimationSetInput` payload: sequential frame
 * ranges per state, resized frame buffer (preserving existing pixel data up
 * to `min(old, new)`), wholesale state machine, layout for fresh state ids,
 * and the resting selection. Pulled out so the proposal-apply path can run
 * the exact same logic without re-dispatching events.
 */
function applyScaffold(ctx: StoreContext, input: ScaffoldAnimationSetInputType): StoreContext {
  let cursor = 0;
  const stateFrames: StateFrameRanges = {};
  for (const s of input.states) {
    const start = cursor;
    const end = cursor + s.frameCount - 1;
    stateFrames[s.id] = frameRange(start, end);
    cursor = end + 1;
  }
  const totalFrames = Math.min(MAX_FRAME_COUNT, Math.max(1, cursor));

  const oldFrames = ctx.frames;
  const oldIds = ctx.frameIds;
  const oldThumbs = ctx.thumbs;
  const frames: Frame[] = [];
  const frameIds: string[] = [];
  const thumbs: string[] = [];
  for (let i = 0; i < totalFrames; i += 1) {
    const oldFrame = oldFrames[i];
    if (oldFrame) {
      frames.push(oldFrame);
      frameIds.push(oldIds[i] ?? crypto.randomUUID());
      thumbs.push(oldThumbs[i] ?? "");
    } else {
      frames.push(emptyFrameLayers());
      frameIds.push(crypto.randomUUID());
      thumbs.push("");
    }
  }

  const machine: StateMachine = {
    initial: input.initial,
    states: input.states.map((s) => ({ id: s.id })),
    transitions: (input.transitions ?? []).map<PxpfTransition>((t) => ({
      from: t.from,
      to: t.to,
      event: t.event,
      trigger: "manual",
    })),
  };

  const layout: MachineLayout = {};
  input.states.forEach((s, i) => {
    const existing = ctx.machineLayout[s.id];
    if (existing) {
      layout[s.id] = existing;
    } else {
      const column = i % 3;
      const row = Math.floor(i / 3);
      layout[s.id] = { x: 100 + column * 220, y: 120 + row * 170 };
    }
  });

  const fps = input.fps ?? ctx.fps;
  const firstLayerId = frames[0]?.[0]?.id ?? "L1";

  return {
    ...ctx,
    frames,
    frameIds,
    thumbs,
    stateMachine: machine,
    machineLayout: layout,
    stateFrames: clampStateFrames(stateFrames, frames.length),
    fps: safeAccept(FpsSchema, fps, ctx.fps, "fps"),
    activeFrame: clampActiveFrame(ctx.activeFrame, frames.length),
    activeLayerId: resolveActiveLayerId(frames[0] ?? [], firstLayerId),
    selectedStateId: null,
    selectedTransitionIndex: null,
  };
}

export type Store = typeof store;
